import { VESSEL_SEARCHER_COLUMNS } from "../constants/vessel-searcher.constants";
import type {
  VesselSearcherResponse,
  VesselSearcherSortDirection,
  VesselSearcherSortKey,
  VesselSearcherVessel,
} from "../models/vessel-searcher.models";
import { formatVesselSearcherCell } from "../utils/vessel-searcher-format.utils";
import { TableCellsIcon } from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";

interface VesselSearcherTableProps {
  response: VesselSearcherResponse | null;
  isLoading: boolean;
  error: string | null;
  useMockData: boolean;
  offset: number;
  pageSize: number;
  sortBy: VesselSearcherSortKey;
  sortDirection: VesselSearcherSortDirection;
  onSort: (sortBy: VesselSearcherSortKey) => void;
  onOffsetChange: (offset: number) => void;
}

const renderCell = (vessel: VesselSearcherVessel, column: (typeof VESSEL_SEARCHER_COLUMNS)[number]) => {
  const value = vessel[column.key];
  const formatted = formatVesselSearcherCell(column, value);

  if (column.key === "lastFixtureDate" && vessel.imosLink && formatted) {
    return <a href={vessel.imosLink} target="_blank" rel="noreferrer" className="font-medium text-green underline-offset-2 hover:underline">{formatted}</a>;
  }

  return formatted;
};

// Renders the complete PBIP column contract with server paging so the Snowflake result size never reaches browser state.
const VesselSearcherTable = ({
  response,
  isLoading,
  error,
  useMockData,
  offset,
  pageSize,
  sortBy,
  sortDirection,
  onSort,
  onOffsetChange,
}: VesselSearcherTableProps) => {
  const total = response?.total ?? 0;
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + pageSize, total);

  return (
    <section className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800" data-test="vessel-searcher-table">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <div className="flex items-center gap-2"><TableCellsIcon className="h-5 w-5 text-green" /><div><h2 className="text-sm font-semibold">Vessel database</h2><p className="text-[11px] text-gray-500 dark:text-gray-400">Detailed particulars and latest inspection data</p></div></div>
        <div className="flex items-center gap-2">
          <span className={twMerge("rounded-full px-3 py-1 text-[11px] font-semibold", useMockData ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800")}>{useMockData ? "Mock data" : "Snowflake data"}</span>
          <span className="rounded-full bg-primary-10 px-3 py-1 text-xs font-semibold text-green dark:bg-teal-950/40">{total.toLocaleString("en-US")} vessels</span>
        </div>
      </div>

      {error && <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">{error}</div>}
      <div className="relative min-h-0 flex-1 overflow-auto">
        {isLoading && <div className="absolute inset-x-0 top-0 z-20 h-0.5 animate-pulse bg-green" aria-label="Loading vessels" />}
        <table className="w-full min-w-[2650px] border-separate border-spacing-0 text-[11px]">
          <thead className="sticky top-0 z-10 bg-gray-50 text-left text-[10px] uppercase tracking-wide text-gray-500 dark:bg-gray-900 dark:text-gray-400">
            <tr>{VESSEL_SEARCHER_COLUMNS.map((column) => {
              const isActiveSort = sortBy === column.key;
              const ariaSort = isActiveSort ? (sortDirection === "asc" ? "ascending" : "descending") : "none";

              return (
                <th key={column.key} aria-sort={ariaSort} className={twMerge("whitespace-nowrap border-b border-gray-200 px-3 py-3 font-semibold dark:border-gray-700", column.alignRight && "text-right")}>
                  <button type="button" onClick={() => onSort(column.key)} className={twMerge("inline-flex items-center gap-1 hover:text-green", column.alignRight && "justify-end")} data-test={`vessel-searcher-sort-${column.key}`}>
                    {column.label}<span aria-hidden="true">{isActiveSort ? (sortDirection === "asc" ? "▲" : "▼") : ""}</span>
                  </button>
                </th>
              );
            })}</tr>
          </thead>
          <tbody>
            {response?.vessels.map((vessel) => (
              <tr key={vessel.imo} className="text-gray-700 odd:bg-white even:bg-gray-50/60 hover:bg-primary-10 dark:text-gray-200 dark:odd:bg-gray-800 dark:even:bg-gray-800/70 dark:hover:bg-teal-950/50">
                {VESSEL_SEARCHER_COLUMNS.map((column) => (
                  <td key={`${vessel.imo}-${column.key}`} className={twMerge("max-w-[210px] whitespace-nowrap border-b border-gray-100 px-3 py-2 dark:border-gray-700", column.alignRight && "text-right tabular-nums", column.wrap && "whitespace-normal leading-4", column.key === "imo" && "font-mono font-medium text-green", column.key === "vesselName" && "font-semibold text-gray-900 dark:text-gray-100")}>{renderCell(vessel, column)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && response && response.vessels.length === 0 && <div className="flex min-h-[240px] items-center justify-center text-sm text-gray-500 dark:text-gray-400">No vessels match the selected filters.</div>}
      </div>

      <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
        <span>{pageStart.toLocaleString("en-US")}-{pageEnd.toLocaleString("en-US")} of {total.toLocaleString("en-US")}</span>
        <div className="flex gap-2">
          <button type="button" disabled={offset === 0 || isLoading} onClick={() => onOffsetChange(Math.max(0, offset - pageSize))} className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600" data-test="vessel-searcher-previous-page">Previous</button>
          <button type="button" disabled={offset + pageSize >= total || isLoading} onClick={() => onOffsetChange(offset + pageSize)} className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600" data-test="vessel-searcher-next-page">Next</button>
        </div>
      </div>
    </section>
  );
};

export default VesselSearcherTable;
